import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Waves } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const getSessionId = () => {
  let id = localStorage.getItem("seatech_chat_session");
  if (!id) {
    id = "sess-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("seatech_chat_session", id);
  }
  return id;
};

const WELCOME = {
  role: "assistant",
  content: "Hi, I'm Finn 🐠 — Seatech's aquarium assistant. Ask me anything about our services, water delivery, pricing, or general fish, coral & aquarium care!",
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(getSessionId());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      const data = await res.json();
      setMessages((m) => [...m, { role: "assistant", content: data.response }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I couldn't reach the server. Please try again or call 602-628-7270." }]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-launcher"
        aria-label="Open chat assistant"
        className="fixed bottom-6 right-6 z-[80] flex h-16 w-16 items-center justify-center rounded-full bg-[var(--aqua)] text-[var(--ink-900)] shadow-[0_12px_40px_-8px_var(--aqua)] transition-all duration-300 hover:scale-105"
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <X className="h-7 w-7" />
            </motion.span>
          ) : (
            <motion.span key="c" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
              <MessageCircle className="h-7 w-7" />
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            data-testid="chat-panel"
            className="fixed bottom-28 right-6 z-[80] flex h-[540px] w-[92vw] max-w-[400px] flex-col overflow-hidden rounded-3xl border border-[var(--foam)]/12 bg-[#0b1024]/95 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.75)] backdrop-blur-2xl"
          >
            {/* Header */}
            <div className="flex items-center gap-3 border-b border-[var(--foam)]/10 bg-[#0b1024]/60 px-5 py-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full border border-[var(--aqua)]/30 bg-[var(--aqua)]/10 text-[var(--aqua)]">
                <Waves className="h-5 w-5" />
              </div>
              <div>
                <p className="font-title text-base font-bold tracking-tight text-[var(--foam)]">Ask Finn</p>
                <p className="font-body text-[11px] font-light uppercase tracking-[0.16em] text-[var(--aqua)]">Seatech AI Assistant</p>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} data-testid="chat-messages" className="flex-1 space-y-4 overflow-y-auto px-4 py-5">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`chat-msg-${m.role}`}
                    className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-3 font-body text-sm font-light leading-relaxed ${
                      m.role === "user"
                        ? "bg-[var(--aqua)] text-[var(--ink-900)]"
                        : "border border-[var(--foam)]/10 bg-[#141a33]/80 text-[var(--foam)]/90"
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="flex items-center gap-1.5 rounded-2xl border border-[var(--foam)]/10 bg-[#141a33]/80 px-4 py-3.5">
                    {[0, 1, 2].map((n) => (
                      <motion.span key={n} animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1, repeat: Infinity, delay: n * 0.2 }} className="h-2 w-2 rounded-full bg-[var(--aqua)]" />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-[var(--foam)]/10 bg-[#0b1024]/60 p-3">
              <div className="flex items-end gap-2 rounded-2xl border border-[var(--foam)]/12 bg-[#141a33]/60 px-3 py-2">
                <textarea
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKey}
                  rows={1}
                  placeholder="Ask about aquariums, fish, services…"
                  className="max-h-24 flex-1 resize-none bg-transparent py-1.5 font-body text-sm font-light text-[var(--foam)] placeholder:text-[var(--foam)]/40 focus:outline-none"
                />
                <button
                  onClick={send}
                  disabled={loading || !input.trim()}
                  data-testid="chat-send"
                  aria-label="Send message"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--aqua)] text-[var(--ink-900)] transition-all duration-200 hover:scale-105 disabled:opacity-40"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
